import UserManagementPanel from "./settings/UserManagementPanel";
import UserProfilePanel from "./settings/UserProfilePanel";
import PushDevicesPanel from "./settings/PushDevicesPanel";
import { PUSH_DEVICES_PANEL_VISIBLE } from "./settings/pushDevicesPanelVisibility";
import SecurityPanel from "./settings/SecurityPanel";
import CompanyPanel from "./settings/CompanyPanel";
import { useTranslation } from "react-i18next";
import APIPanel from "./settings/APIPanel";
import { useAtom } from "jotai";
import { authenticationAtom } from "./State";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import DataManagementPanel from "./settings/DataManagementPanel";
import ProductManagementPanel from "./settings/ProductManagementPanel";
import AlertManagementPanel from "./settings/AlertManagementPanel";
import AssetArchivePanel from "./settings/AssetArchivePanel";
import AssetInventoryPanel from "./settings/AssetInventoryPanel";
import DefectsPanel from "./settings/defects/DefectsPanel";
import BaseliningPanel from "./settings/baselining/BaseliningPanel";
import PartnerManagementPanel from "./settings/PartnerManagementPanel";
import OperationsDashboard from "./dashboards/OperationsDashoard";
import ProjectPanel from "./settings/ProjectPanel";
import HeatmapPage from "./heatmap/HeatmapPage";
import ContentPanel from "./components/ContentPanel";
import GatewayPanel from "./administration/GatewayPanel";
import ProcessManagementPanel from "./settings/ProcessPanel";
import AgentPanel from "./settings/AgentPanel";
import FileManagementPanel from "./settings/FileManagementPanel";
import { configuration } from "./Configuration";
import OrdersSections from "./settings/OrdersSections";
import { OrdersSection, sectionForMovedPanel } from "./hooks/settingsPanels";
import { useRoleAccess } from "./hooks/useRoleAccess";
import ObligationActivationPanel from "./settings/operator-obligations/ObligationActivationPanel";
import TenderSearchProfilesPanel from "./administration/operator-responsibility/TenderSearchProfilesPanel";
import type { AppShellContext } from "./components/AppShell";

/**
 * Panel-Inhalt der Einstellungen. Sidebar, Kopfzeile und die Navigations-/
 * Zugriffs-Gates liegen im AppShell (Layout-Route); diese Seite rendert nur
 * noch das aktive Panel und setzt ihren Kopfzeilentitel über den
 * Outlet-Context.
 */
export default function SettingsPage() {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const roleAccess = useRoleAccess();
  const { setTitle, hasOperatorObligationsAccess, hasProductManagementAccess, hasBaseliningAccess, hasTenderToolsAccess } =
    useOutletContext<AppShellContext>();

  /**
   * Der gezeigte Bereich wird aus der Adresse **abgeleitet**, nicht in einen Zustand kopiert.
   *
   * Er stand hier einmal in `useState` und wurde von einem Effekt nachgezogen. Ein Effekt läuft
   * nach dem Zeichnen: Ein Klick in der Seitenleiste zeichnete deshalb erst noch einmal den
   * **alten** Bereich, und erst der Durchlauf danach zeigte den neuen. Wer den alten gerade
   * verlässt, wartet damit auf dessen vollständigen Renderdurchlauf — bei einer Seite mit vier
   * Tabellen ist das der spürbare Teil des Wechsels, und mit jeder Ladeoperation, die in diesem
   * Durchlauf noch angestoßen wird, wächst er.
   *
   * Abgeleitet ist der Wechsel dagegen sofort: Dieselbe Adresse, die der Klick setzt, bestimmt in
   * demselben Durchlauf, was zu sehen ist. Ein Bereich wechselt, was auch immer sonst gerade lädt.
   */
  const panelFromUrl = searchParams.get("panel") || "userProfile";

  // Nur bei einer *endgültigen* Absage umlenken, nicht solange die Prüfung noch läuft
  // (`undefined`) — sonst schöbe eine langsame Abfrage den Benutzer aus seinem Bereich.
  const productManagementDenied =
    !roleAccess.canSeeProductManagement ||
    (hasProductManagementAccess === false &&
      !configuration.shouldSeeProductManagement(authentication?.customer));
  const denied =
    (panelFromUrl === "operatorObligations" &&
      (!roleAccess.canSeeOperations || hasOperatorObligationsAccess === false)) ||
    (panelFromUrl === "baselining" && (!roleAccess.canSeeOperations || hasBaseliningAccess === false)) ||
    ((panelFromUrl === "productManagement" || panelFromUrl === "productImages") && productManagementDenied);
  // Abrechnung und Statistiken sind Reiter der Bestellungen. Ein alter Link (`?panel=billing`)
  // zeigt sofort den Reiter; die Adresse zieht der Effekt unten nach.
  const movedSection = sectionForMovedPanel(panelFromUrl);
  const selectedPanel = denied ? "userProfile" : movedSection ? "orders" : panelFromUrl;
  const ordersSection = movedSection ?? searchParams.get("section");

  /**
   * Die Adresse einer Absage nachziehen.
   *
   * Der Bereich steht schon auf `userProfile`, ohne auf diesen Effekt zu warten — er richtet nur
   * noch die Adresse danach aus, damit ein Neuladen dieselbe Seite zeigt.
   */
  useEffect(() => {
    if (!denied) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        next.set("panel", "userProfile");

        return next;
      },
      { replace: true },
    );
  }, [denied, setSearchParams]);

  useEffect(() => {
    if (!movedSection) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        next.set("panel", "orders");
        next.set("section", movedSection);

        return next;
      },
      { replace: true },
    );
  }, [movedSection, setSearchParams]);

  const setOrdersSection = (section: OrdersSection) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        next.set("section", section);

        return next;
      },
      { replace: true },
    );

  const getTitle = () => {
    switch (selectedPanel) {
      case "userProfile":
        return t("settingsPanel.panelTitles.userProfile");
      case "securityProfile":
        return t("settingsPanel.panelTitles.securityProfile");
      case "companyProfile":
        return t("settingsPanel.panelTitles.companyProfile");
      case "teamManagement":
        return t("settingsPanel.panelTitles.teamManagement");
      case "pushDevices":
        return t("settingsPanel.panelTitles.pushDevices");
      case "api":
        return t("settingsPanel.panelTitles.api");
      case "projects":
        return t("settingsPanel.panelTitles.projects");
      case "scoringHeatmap":
        return t("settingsPanel.panelTitles.scoringHeatmap");
      case "productManagement":
        return t("settingsPanel.panelTitles.productManagement");
      case "telemetry":
        return t("settingsPanel.panelTitles.telemetry");
      case "alerts":
        return t("settingsPanel.panelTitles.alerts");
      case "assetArchive":
        return t("settingsPanel.panelTitles.assetArchive");
      case "operationalState":
        return t("settingsPanel.panelTitles.operationalState");
      case "assetInventory":
        return t("settingsPanel.panelTitles.assetInventory");
      case "defects":
        return t("settingsPanel.panelTitles.defects");
      case "baselining":
        return t("settingsPanel.panelTitles.baselining");
      case "partnerManagement":
        return t("settingsPanel.panelTitles.partnerManagement");
      case "dataManagement":
        return t("settingsPanel.panelTitles.dataManagement");
      case "processManagement":
        return t("settingsPanel.panelTitles.processManagement");
      case "operatorObligations":
        return t("settingsPanel.operatorObligations");
      case "agent":
        return t("settingsPanel.panelTitles.agent");
      case "fileManagement":
        return t("settingsPanel.panelTitles.fileManagement");
      case "orders":
        return t("settingsPanel.panelTitles.orders");
      case "tenderSearchProfiles":
        return t("settingsPanel.panelTitles.tenderSearchProfiles");
      default:
        return t("settingsPanel.settings");
    }
  };

  const title = getTitle();

  useEffect(() => {
    setTitle(title);
  }, [title, setTitle]);

  return (
    <ContentPanel
      backgroundColor="transparent"
      // Die Dateiverwaltung füllt als Vollflächen-Panel den Bereich randlos.
      paddingClassName={selectedPanel === "fileManagement" || selectedPanel === "scoringHeatmap" ? "p-0" : undefined}
    >
      {/* min-h-0 + flex-1, damit Vollhöhen-Panels (Dateiverwaltung) den Platz
          bis zur Fensterunterkante füllen können; inhaltshohe Panels bleiben
          davon unberührt. */}
      <div className="flex min-h-0 flex-1 flex-col">
        {(() => {
          switch (selectedPanel) {
            case "userProfile":
              return <UserProfilePanel />;
            case "securityProfile":
              return <SecurityPanel />;
            case "companyProfile":
              return roleAccess.canSeeCompanyProfile ? <CompanyPanel /> : null;
            case "teamManagement":
              return roleAccess.canSeeTeamManagement ? <UserManagementPanel /> : null;
            case "pushDevices":
              return PUSH_DEVICES_PANEL_VISIBLE && roleAccess.canSeeTeamManagement ? <PushDevicesPanel /> : null;
            case "orders":
              return roleAccess.canSeeOrders ? (
                <OrdersSections section={ordersSection} onSectionChange={setOrdersSection} />
              ) : null;
            case "api":
              return roleAccess.canSeeApiKeys ? <APIPanel /> : null;
            case "projects":
              return roleAccess.canSeeProjects ? <ProjectPanel /> : null;
            case "scoringHeatmap":
              return roleAccess.canSeeProjects ? <HeatmapPage /> : null;
            case "productManagement":
              return roleAccess.canSeeProductManagement &&
                (configuration.shouldSeeProductManagement(authentication.customer) ||
                  hasProductManagementAccess === true) ? (
                <ProductManagementPanel />
              ) : null;
            case "telemetry":
              return roleAccess.canSeeOperations ? <GatewayPanel /> : null;
            case "alerts":
              return roleAccess.canSeeOperations ? <AlertManagementPanel /> : null;
            case "assetArchive":
              return roleAccess.canSeeOperations ? <AssetArchivePanel /> : null;
            case "operationalState":
              return roleAccess.canSeeOperations ? <OperationsDashboard /> : null;
            case "assetInventory":
              return roleAccess.canSeeOperations ? <AssetInventoryPanel /> : null;
            case "defects":
              return roleAccess.canSeeOperations ? <DefectsPanel /> : null;
            case "baselining":
              // Ohne Bestellung des Angebots „FM-Baselining“ lenkt `denied` oben auf das Profil um.
              return roleAccess.canSeeOperations && hasBaseliningAccess !== false ? <BaseliningPanel /> : null;
            case "partnerManagement":
              return roleAccess.isCustomerAdmin && configuration.shouldSeePartnerManagement(authentication?.customer) ? <PartnerManagementPanel /> : null;
            case "dataManagement":
              return roleAccess.canSeeDataManagement ? <DataManagementPanel /> : null;
            case "processManagement":
              return roleAccess.canSeeOperations ? <ProcessManagementPanel /> : null;
            case "operatorObligations":
              return roleAccess.canSeeOperations && hasOperatorObligationsAccess === true ? (
                <ObligationActivationPanel />
              ) : null;
            case "agent":
              return roleAccess.canSeeAgent ? <AgentPanel /> : null;
            case "fileManagement":
              return roleAccess.canSeeFileManagement ? <FileManagementPanel /> : null;
            case "tenderSearchProfiles":
              return hasTenderToolsAccess ? <TenderSearchProfilesPanel refreshSignal={0} /> : null;
          }
        })()}
      </div>
    </ContentPanel>
  );
}
